import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { CheckoutLayout } from "../components/CheckoutLayout";
import { PixPaymentSection } from "../components/Pix";
import { useCheckoutStore } from "../store";
import { googleAdsTracker } from "../services/googleAds";

export function PixPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { paymentId, setPaymentId } = useCheckoutStore();
  const [now, setNow] = useState(Date.now());

  const searchParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const urlPaymentId = searchParams.get("payment") || searchParams.get("id") || searchParams.get("paymentId") || searchParams.get("payment_id");
  const activePaymentId = urlPaymentId || paymentId;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (urlPaymentId && urlPaymentId !== paymentId) {
      setPaymentId(urlPaymentId);
    }
  }, [urlPaymentId, paymentId, setPaymentId]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const paymentQuery = useQuery({
    queryKey: ["payment", activePaymentId],
    enabled: !!activePaymentId,
    queryFn: () => api.getPayment(activePaymentId!)
  });

  const statusQuery = useQuery({
    queryKey: ["payment-status", activePaymentId],
    enabled: !!activePaymentId && !["PAID", "EXPIRED", "FAILED", "CANCELLED"].includes(paymentQuery.data?.status ?? ""),
    queryFn: () => api.paymentStatus(activePaymentId!),
    refetchInterval: (query) => (query.state.data?.terminal ? false : 4000)
  });

  const approveMutation = useMutation({
    mutationFn: () => api.approveDevPayment(activePaymentId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["payment", activePaymentId] });
      await queryClient.invalidateQueries({ queryKey: ["payment-status", activePaymentId] });
      navigate("/checkout/success");
    }
  });

  useEffect(() => {
    const status = statusQuery.data?.status ?? paymentQuery.data?.status;
    if (status === "PAID") navigate("/checkout/success");
    if (status === "EXPIRED") navigate("/checkout/pending");
    if (status === "FAILED" || status === "CANCELLED") navigate("/checkout/error");
  }, [statusQuery.data?.status, paymentQuery.data?.status, navigate]);

  const payment = paymentQuery.data;
  const secondsLeft = useMemo(() => (payment ? Math.floor((new Date(payment.expiresAt).getTime() - now) / 1000) : 0), [payment, now]);

  const { checkoutId } = useCheckoutStore();

  const sessionQuery = useQuery({
    queryKey: ["session", checkoutId],
    enabled: !!checkoutId,
    queryFn: () => api.getSession(checkoutId!)
  });

  useEffect(() => {
    if (payment?.id) {
      api.getConfig().then((config) => {
        if (config?.googleAds) {
          googleAdsTracker.init(config.googleAds);

          const session = sessionQuery.data;
          const items = (session?.items || []).map((item: any) => ({
            id: item.shopifyVariantGid || item.id,
            name: item.title,
            price: item.unitPrice,
            quantity: item.quantity
          }));

          googleAdsTracker.trackAddPaymentInfo("PIX", items, payment.amount, "BRL");

          const trackedKey = `melhot_tracked_purchase_${payment.id}`;
          if (!sessionStorage.getItem(trackedKey)) {
            sessionStorage.setItem(trackedKey, "true");

            const addr = (session as any)?.address || {};
            const customerData = {
              email: session?.email,
              phone: session?.phone,
              firstName: session?.firstName,
              lastName: session?.lastName,
              street: addr.street,
              city: addr.city,
              state: addr.state,
              zip: addr.postalCode,
              country: addr.country || "BR"
            };

            googleAdsTracker.trackPurchase(
              payment.id,
              payment.amount,
              items,
              customerData,
              "BRL"
            );
          }
        }
      }).catch(() => {});
    }
  }, [payment?.id, payment?.amount, sessionQuery.data]);

  if (!paymentId) {
    return (
      <CheckoutLayout
        aside={<div />}
        main={
          <div className="px-4 py-8 sm:px-0">
            <Link to="/checkout" className="font-semibold text-trust">
              Voltar ao checkout
            </Link>
          </div>
        }
      />
    );
  }
  if (paymentQuery.isError) {
    return (
      <CheckoutLayout
        aside={<div />}
        main={
          <div className="mx-auto max-w-xl px-4 py-12 text-center sm:px-0">
            <h2 className="mb-2 text-lg font-bold text-slate-900">Pagamento não encontrado</h2>
            <p className="mb-6 text-sm text-slate-600">Não foi possível carregar este pagamento. O link pode ter expirado ou estar incorreto.</p>
            <Link to="/checkout" className="inline-flex h-11 items-center justify-center rounded-lg bg-black px-6 font-bold text-white">
              Voltar ao checkout
            </Link>
          </div>
        }
      />
    );
  }
  if (!payment) {
    return (
      <CheckoutLayout
        aside={<div />}
        main={<div className="px-4 py-8 text-sm text-slate-600 sm:px-0">Carregando pagamento...</div>}
      />
    );
  }

  return (
    <CheckoutLayout
      aside={<div />}
      main={
        <div className="mx-auto max-w-xl space-y-4 px-4 pb-8 pt-3 sm:px-0">
          <PixPaymentSection payment={{ ...payment, status: statusQuery.data?.status ?? payment.status }} secondsLeft={secondsLeft} />
          {import.meta.env.VITE_APP_ENV !== "production" && (
            <button onClick={() => approveMutation.mutate()} className="w-full border border-line bg-white px-4 py-3 text-sm font-semibold">
              Aprovar PIX mock
            </button>
          )}
        </div>
      }
    />
  );
}
